<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowUp, Download, FileText, Image as ImageIcon, LoaderCircle, Maximize2, MessageSquareText, Radio, Search, X } from 'lucide-vue-next'

import type { Channel, SenderKind, SenderStat, TelegramMessage } from '@/types/monitor'
import { formatBytes, formatLocalDateTime, stripMediaPrefix } from '@/utils/format'

const props = defineProps<{
  messages: TelegramMessage[]
  channels: Channel[]
  senderStats: SenderStat[]
  realtime: boolean
  loadingOlder: boolean
  hasMore: boolean
}>()

const emit = defineEmits<{
  loadOlder: []
  senderFilter: [senderId: number | null]
}>()

const { t } = useI18n()
const query = ref('')
const source = ref('')
const sender = ref('')
const selectedMedia = ref<TelegramMessage | null>(null)
const loadMoreSentinel = ref<HTMLElement | null>(null)
const showJumpLatest = ref(false)
let intersectionObserver: IntersectionObserver | null = null
let scrollContainer: HTMLElement | null = null

const filtered = computed(() => {
  const needle = query.value.trim().toLowerCase()
  return props.messages.filter((message) => {
    if (source.value && message.channel_username !== source.value) return false
    if (sender.value && String(message.sender_telegram_id ?? '') !== sender.value) return false
    if (!needle) return true
    return `${message.channel_name} ${message.channel_username ?? ''} ${message.sender_name ?? ''} ${message.sender_username ?? ''} ${message.text} ${message.media_caption ?? ''}`
      .toLowerCase()
      .includes(needle)
  })
})

const topSenders = computed(() => [...props.senderStats].sort((left, right) => {
  const priority = (kind?: SenderKind): number => kind === 'human' ? 0 : kind === 'bot' ? 1 : 2
  return priority(left.kind) - priority(right.kind) || right.message_count - left.message_count
}).slice(0, 8))
const senderMessageTotal = computed(() => props.senderStats.reduce((total, stat) => total + stat.message_count, 0))
const humanSenderCount = computed(() => props.senderStats.filter((stat) => stat.kind === 'human').length)
const botSenderCount = computed(() => props.senderStats.filter((stat) => stat.kind === 'bot').length)

function senderStatName(stat: SenderStat): string {
  return stat.name?.trim() || stat.username?.trim() || t('workspace.unknownSender')
}

function senderKindLabel(kind?: SenderKind): string {
  switch (kind) {
    case 'bot': return t('workspace.botTool')
    case 'channel': return t('workspace.channelSender')
    case 'group': return t('workspace.groupSender')
    case 'human': return t('workspace.humans')
    default: return t('workspace.unknownSenderKind')
  }
}

function senderKindClass(kind?: SenderKind): string {
  return `tm-sender-kind--${kind ?? 'unknown'}`
}

function senderShare(stat: SenderStat): string {
  if (senderMessageTotal.value <= 0) return '0%'
  return `${((stat.message_count / senderMessageTotal.value) * 100).toFixed(1)}%`
}

function selectSender(stat: SenderStat): void {
  const key = String(stat.telegram_id)
  sender.value = sender.value === key ? '' : key
}

function senderName(message: TelegramMessage): string {
  return message.sender_name?.trim() || message.channel_name || t('workspace.unknownSender')
}

function avatarLetter(message: TelegramMessage): string {
  return senderName(message).slice(0, 1).toUpperCase() || '?'
}

function messageText(message: TelegramMessage): string {
  if (message.has_media && message.media_caption?.trim()) return message.media_caption.trim()
  return stripMediaPrefix(message.text ?? '').trim()
}

function isImage(message: TelegramMessage): boolean {
  return message.media_type === 'photo' || message.media_type === 'gif' || message.mime_type?.startsWith('image/') === true
}

function isVideo(message: TelegramMessage): boolean {
  return message.media_type === 'video' || message.mime_type?.startsWith('video/') === true
}

function retryAvatar(event: Event): void {
  const target = event.currentTarget
  if (!(target instanceof HTMLImageElement)) return
  const retries = Number(target.dataset.retries ?? '0')
  if (retries >= 4) {
    target.hidden = true
    return
  }
  target.dataset.retries = String(retries + 1)
  const base = target.src.split('?')[0]
  window.setTimeout(() => {
    target.src = `${base}?retry=${Date.now()}`
  }, 1500 * (retries + 1))
}

function openMedia(message: TelegramMessage): void {
  if (!message.media_url) return
  selectedMedia.value = message
  document.documentElement.classList.add('tm-media-open')
}

function closeMedia(): void {
  selectedMedia.value = null
  document.documentElement.classList.remove('tm-media-open')
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && selectedMedia.value) closeMedia()
}

function onMainScroll(): void {
  showJumpLatest.value = (scrollContainer?.scrollTop ?? 0) > 560
}

function jumpToLatest(): void {
  scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' })
}

async function observePaginationSentinel(): Promise<void> {
  await nextTick()
  intersectionObserver?.disconnect()
  if (!loadMoreSentinel.value || !props.hasMore) return
  intersectionObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !props.loadingOlder && props.hasMore) {
        emit('loadOlder')
      }
    },
    { root: scrollContainer, rootMargin: '420px 0px', threshold: 0.01 },
  )
  intersectionObserver.observe(loadMoreSentinel.value)
}

watch(() => props.hasMore, () => void observePaginationSentinel())
watch(sender, (value) => emit('senderFilter', value ? Number(value) : null))

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  scrollContainer = document.querySelector<HTMLElement>('.tm-main')
  scrollContainer?.addEventListener('scroll', onMainScroll, { passive: true })
  void observePaginationSentinel()
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  scrollContainer?.removeEventListener('scroll', onMainScroll)
  intersectionObserver?.disconnect()
  document.documentElement.classList.remove('tm-media-open')
})
</script>

<template>
  <section class="tm-view tm-messages-view">
    <header class="tm-page-header">
      <div>
        <h1>{{ t('workspace.messages') }}</h1>
        <p>{{ t('workspace.monitoringOnly') }}</p>
      </div>
      <div class="tm-health-pill" :class="{ 'tm-health-pill--ok': realtime }">
        <span></span>{{ realtime ? t('workspace.realtime') : t('workspace.disconnected') }}
      </div>
    </header>

    <div class="tm-toolbar">
      <div class="tm-search-field"><Search :size="17" /><input v-model="query" :placeholder="t('workspace.searchMessages')" /></div>
      <select v-model="source" class="tm-select">
        <option value="">{{ t('workspace.allSources') }}</option>
        <option v-for="channel in channels" :key="channel.username" :value="channel.username">
          {{ channel.title || channel.username }}
        </option>
      </select>
      <select v-model="sender" class="tm-select tm-select--sender">
        <option value="">{{ t('workspace.allSenders') }}</option>
        <option v-for="stat in senderStats" :key="stat.telegram_id" :value="String(stat.telegram_id)">
          [{{ senderKindLabel(stat.kind) }}] {{ senderStatName(stat) }} · {{ stat.message_count.toLocaleString() }}
        </option>
      </select>
      <span class="tm-toolbar-count">{{ filtered.length.toLocaleString() }}</span>
    </div>

    <section v-if="topSenders.length" class="tm-sender-stats" :aria-label="t('workspace.senderStats')">
      <header>
        <div><strong>{{ t('workspace.senderStats') }}</strong><span>{{ t('workspace.senderStatsHint') }}</span></div>
        <span>{{ humanSenderCount }} {{ t('workspace.humans') }} · {{ botSenderCount }} {{ t('workspace.bots') }} · {{ senderStats.length.toLocaleString() }} {{ t('workspace.identifiedSenders') }}</span>
      </header>
      <div class="tm-sender-stat-list">
        <button
          v-for="stat in topSenders"
          :key="stat.telegram_id"
          type="button"
          class="tm-sender-stat-card"
          :class="{ active: sender === String(stat.telegram_id) }"
          @click="selectSender(stat)"
        >
          <span class="tm-sender-stat-avatar">
            <span>{{ senderStatName(stat).slice(0, 1).toUpperCase() || '?' }}</span>
            <img v-if="stat.avatar_url" :src="stat.avatar_url" :alt="senderStatName(stat)" loading="lazy" @error="retryAvatar" />
          </span>
          <span class="tm-sender-stat-copy">
            <strong>{{ senderStatName(stat) }} <em v-if="stat.kind !== 'human'" class="tm-sender-kind" :class="senderKindClass(stat.kind)">{{ senderKindLabel(stat.kind) }}</em></strong>
            <small v-if="stat.username">@{{ stat.username }}</small>
          </span>
          <span class="tm-sender-stat-number"><strong>{{ stat.message_count.toLocaleString() }}</strong><small>{{ senderShare(stat) }}</small></span>
        </button>
      </div>
    </section>

    <div class="tm-card tm-message-browser">
      <div v-if="filtered.length" class="tm-message-list">
        <article v-for="message in filtered" :key="message.id" class="tm-message-row tm-message-row--large">
          <div class="tm-message-avatar tm-message-avatar--sender">
            <span>{{ avatarLetter(message) }}</span>
            <img
              v-if="message.sender_avatar_url"
              :src="message.sender_avatar_url"
              :alt="senderName(message)"
              loading="lazy"
              @error="retryAvatar"
            />
          </div>
          <div class="tm-message-body">
            <div class="tm-message-head">
              <div class="tm-message-identity">
                <strong>{{ senderName(message) }}</strong>
                <span v-if="message.sender_username" class="tm-message-username">@{{ message.sender_username }}</span>
                <span v-if="message.sender_kind && message.sender_kind !== 'human'" class="tm-sender-kind" :class="senderKindClass(message.sender_kind)">{{ senderKindLabel(message.sender_kind) }}</span>
                <span v-if="message.via_bot_telegram_id" class="tm-sender-kind tm-sender-kind--via">{{ t('workspace.viaBot') }}</span>
                <span class="tm-message-source">{{ message.channel_name }}</span>
              </div>
              <time>{{ formatLocalDateTime(message.published_at) }}</time>
            </div>
            <p v-if="messageText(message)" class="tm-message-copy">{{ messageText(message) }}</p>

            <div v-if="message.has_media && message.media_url" class="tm-message-media">
              <button
                v-if="isImage(message)"
                class="tm-message-image-button"
                type="button"
                @click="openMedia(message)"
              >
                <img :src="message.media_thumbnail_url || message.media_url" :alt="t('workspace.imageAlt')" loading="lazy" />
                <span class="tm-media-expand"><Maximize2 :size="15" /></span>
              </button>
              <div v-else-if="isVideo(message)" class="tm-message-video-frame">
                <video
                  class="tm-message-video"
                  :src="message.media_url"
                  controls
                  preload="metadata"
                />
                <button class="tm-media-expand" type="button" :title="t('workspace.expandMedia')" @click="openMedia(message)">
                  <Maximize2 :size="15" />
                </button>
              </div>
              <a v-else class="tm-message-attachment" :href="message.media_url" target="_blank" rel="noreferrer">
                <FileText :size="20" />
                <div><strong>{{ t('workspace.attachment') }}</strong><span>{{ message.mime_type || message.media_type || 'file' }}</span></div>
              </a>
            </div>

            <div v-if="message.has_media" class="tm-message-media-meta">
              <span><ImageIcon :size="13" />{{ message.media_type || 'media' }}</span>
              <span v-if="message.media_size_bytes">{{ formatBytes(message.media_size_bytes) }}</span>
              <a v-if="message.media_url" :href="message.media_url" target="_blank" rel="noreferrer">{{ t('workspace.openMedia') }}</a>
            </div>
          </div>
        </article>
      </div>
      <div v-else class="tm-empty-state tm-empty-state--page">
        <MessageSquareText :size="36" /><p>{{ t('workspace.noMessages') }}</p><Radio :size="18" />
      </div>
      <div v-if="messages.length" ref="loadMoreSentinel" class="tm-message-pagination">
        <template v-if="loadingOlder"><LoaderCircle class="tm-spin" :size="17" /><span>{{ t('workspace.loadingOlder') }}</span></template>
        <span v-else-if="hasMore">{{ t('workspace.scrollForOlder') }}</span>
        <span v-else>{{ t('workspace.historyEnd') }}</span>
      </div>
    </div>

    <button v-if="showJumpLatest" class="tm-jump-latest" type="button" @click="jumpToLatest">
      <ArrowUp :size="16" />{{ t('workspace.jumpLatest') }}
    </button>
  </section>

  <Teleport to="body">
    <Transition name="tm-lightbox">
      <div v-if="selectedMedia?.media_url" class="tm-lightbox" role="dialog" aria-modal="true" @click.self="closeMedia">
        <button class="tm-lightbox-close" type="button" :title="t('common.close')" @click="closeMedia"><X :size="21" /></button>
        <div class="tm-lightbox-dialog">
          <div class="tm-lightbox-stage">
            <img
              v-if="selectedMedia && isImage(selectedMedia)"
              :src="selectedMedia.media_url || undefined"
              :alt="t('workspace.imageAlt')"
            />
            <video
              v-else-if="selectedMedia && isVideo(selectedMedia)"
              :src="selectedMedia.media_url || undefined"
              controls
              autoplay
            />
          </div>
          <footer v-if="selectedMedia" class="tm-lightbox-footer">
            <div>
              <strong>{{ senderName(selectedMedia) }}</strong>
              <span v-if="selectedMedia.sender_username">@{{ selectedMedia.sender_username }}</span>
              <span>{{ selectedMedia.channel_name }} · {{ formatLocalDateTime(selectedMedia.published_at) }}</span>
            </div>
            <a :href="selectedMedia.media_url || undefined" target="_blank" rel="noreferrer"><Download :size="15" />{{ t('workspace.openOriginal') }}</a>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
